import gql from 'graphql-tag';
import apolloClient from '../apollo';

/**
 * State
 */
const getDefaultState = () => {
  return {
    itemInventories: [],
    itemInventoryCount: 0,
  };
};
const state = getDefaultState();

/**
 * Defines the fields which are requested for an itemInventory
 * used for all queries which return an itemInventory
 * @type {DocumentNode}
 */
const ITEM_INVENTORY_REQUEST_FIELDS = gql`
  fragment itemInventoryFields on ItemInventory {
    _id
    item {
      _id
      number
      description
      unit
      type
    }
    count
    project {
      id
      number
      customer {
        lastname
        firstname
      }
    }
    storage {
      number
      name
      itemStorageType
    }
    createdAt
    updatedAt
  }
`;

const ITEM_INVENTORY_LIST_QUERY = gql`
  query itemInventoriesPaginated(
    $pagination: PaginationInput
    $sorting: SortingInput
    $filters: ItemInventoryFilterInput
  ) {
    itemInventoriesPaginated(pagination: $pagination, sorting: $sorting, filters: $filters) {
      itemInventories {
        ...itemInventoryFields
      }
      itemInventoryCount
    }
  }
  ${ITEM_INVENTORY_REQUEST_FIELDS}
`;

/** Inventory list of the block project-item-inventory; the server restricts it to the project. */
const PROJECT_ITEM_INVENTORY_LIST_QUERY = gql`
  query projectItemInventories(
    $projectNumber: Int!
    $pagination: PaginationInput
    $sorting: SortingInput
    $filters: ItemInventoryFilterInput
  ) {
    projectByNumber(projectNumber: $projectNumber) {
      id
      number
      blocks {
        itemInventory {
          id
          number
          itemInventories(pagination: $pagination, sorting: $sorting, filters: $filters) {
            itemInventories {
              ...itemInventoryFields
            }
            itemInventoryCount
          }
        }
      }
    }
  }
  ${ITEM_INVENTORY_REQUEST_FIELDS}
`;

/**
 * Request of the inventory list: with a project number the block project-item-inventory, without
 * it the global query itemInventoriesPaginated. `pagesOf` returns the `ItemInventoryPages`.
 */
const itemInventoryListRequest = (projectNumber) => {
  if (projectNumber) {
    return {
      query: PROJECT_ITEM_INVENTORY_LIST_QUERY,
      pagesOf: (data) => data.projectByNumber.blocks.itemInventory.itemInventories,
    };
  }
  return { query: ITEM_INVENTORY_LIST_QUERY, pagesOf: (data) => data.itemInventoriesPaginated };
};

/**
 * Actions
 */
const actions = {
  /**
   * Fetch a paginated, sorted and filtered itemInventory list. With `projectNumber` the
   * inventories of the block project-item-inventory, without it the global list (view Inventar).
   * @param {*} param0
   * @param {*} payload
   */
  async fetchItemInventoriesPaginated(
    { commit, dispatch },
    { pagination, sorting, filters, projectNumber }
  ) {
    const { query, pagesOf } = itemInventoryListRequest(projectNumber);
    try {
      const response = await apolloClient.query({
        query,
        variables: {
          ...(projectNumber ? { projectNumber } : {}),
          pagination: pagination,
          sorting: sorting,
          filters: filters,
        },
        fetchPolicy: 'network-only',
      });
      const itemInventoryPages = pagesOf(response.data);
      commit('SET_ITEM_INVENTORIES', itemInventoryPages.itemInventories);
      commit('SET_ITEM_INVENTORY_COUNT', itemInventoryPages.itemInventoryCount);
    } catch (err) {
      console.log(err);
      throw new Error(err);
    }
  },
};

/**
 * Mutations
 */
const mutations = {
  SET_DEFAULT_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  SET_ITEM_INVENTORIES: (state, itemInventories) =>
    itemInventories ? (state.itemInventories = itemInventories) : (state.itemInventories = []),
  SET_ITEM_INVENTORY_COUNT: (state, itemInventoryCount) =>
    (state.itemInventoryCount = itemInventoryCount),
};

/**
 * Getters
 */
const getters = {
  getItemInventories: (state) => state.itemInventories,
  getItemInventory: (state) => (itemInventoryId) =>
    state.itemInventories.find((itemInventory) => itemInventory._id == itemInventoryId),
  getItemInventoryCount: (state) => state.itemInventoryCount,
};

export default {
  state,
  getters,
  actions,
  mutations,
};
